/**
 * useCurrentUser — resolves auth state from MeResponse (via useMe / TanStack Query).
 *
 * MeResponse.role is a single string (e.g. 'ROLE_ADMIN').
 * MeResponse.permissions is string[] (e.g. ['USER_READ', 'USER_WRITE']).
 *
 * Multi-session note: refresh tokens are independent per device; this hook
 * always reads from the current device's session only. No cross-session state
 * needed on the FE side.
 */
import { useMe } from './useMe';

export interface CurrentUser {
  id: string;
  email: string;
  /** Single-element array wrapping MeResponse.role for uniform API. */
  roles: string[];
  /** MeResponse.permissions array as-is from BE JWT / /auth/me. */
  permissions: string[];
}

export function useCurrentUser(): CurrentUser | null {
  const { data } = useMe();
  if (!data) return null;
  return {
    id: String(data.id),
    email: data.email,
    roles: [data.role],
    permissions: data.permissions ?? [],
  };
}

export function useHasRole(role: string): boolean {
  const u = useCurrentUser();
  return !!u?.roles.includes(role);
}

export function useHasPermission(perm: string): boolean {
  const u = useCurrentUser();
  if (!perm) return true; // no restriction specified
  return !!u?.permissions.includes(perm);
}
